import { Box, CardActionArea, CardContent, Typography } from '@mui/material';
import { IResourceAllocationData } from '../type';
import { useNavigate } from 'react-router';
import MouseOverPopover from '../../common/widgets/mouse-over-popover';
import AvatarImage from '../../common/avatar-image';
import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MEMBER_SKILLS } from '../gql';

interface PropsType {
  resourceAllocations: IResourceAllocationData;
}

const ToolsIcon1: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <img
    src="/images/ResourceAllocation/ToolsIcon1.svg"
    alt="Tools Icon"
    style={style}
  />
);

const ToolsIcon2: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <img
    src="/images/ResourceAllocation/ToolsIcon2.svg"
    alt="Tools Icon"
    style={style}
  />
);

const ResourceAllocationCard = ({
  resourceAllocations
}: PropsType): JSX.Element => {
  const navigate = useNavigate();

  const [memberId, setMemberId] = useState<string>();
  const [memberSkillsIdPendingApprovals, setMemberSkillsIdPendingApprovals] =
    useState<string[]>([]);

  const { data: memberSkillsData } = useQuery(GET_MEMBER_SKILLS, {
    variables: { id: memberId }
  });

  useEffect(() => {
    if (resourceAllocations) {
      setMemberId(resourceAllocations.member.id);
    }
  }, [resourceAllocations]);

  useEffect(() => {
    if (memberSkillsData) {
      const pendingApprovals: string[] = memberSkillsData.getMemberSkills
        .filter((skill: { status: string }) => skill.status === 'APPROVED')
        .map((skill: { id: string }) => skill.id);
      setMemberSkillsIdPendingApprovals(pendingApprovals);
    }
  }, [memberSkillsData]);

  const filteredResourceAllocationsProjects =
    resourceAllocations.projects.filter(
      (project) => project.memberStatus !== 'REJECTED'
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '14px ,12px',
        border: '1px solid #C1C5CD',
        top: '0.5px',
        borderRadius: '12px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <CardActionArea
        onClick={() =>
          navigate(
            `/resource-allocations/${resourceAllocations.member.displayName}`,
            {
              state: { resourceAllocations }
            }
          )
        }
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '9px'
              }}
            >
              <Box>
                <AvatarImage
                  providerId={
                    resourceAllocations.member.userPrincipalName ?? ''
                  }
                  alt={resourceAllocations.member.displayName}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Be Vietnam Pro',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '20px',
                    letter: '0.25px',
                    color: '#000000'
                  }}
                >
                  {resourceAllocations.member.displayName}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Be Vietnam Pro',
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0.5px',
                    fontWeight: 400,
                    color: '#44474E',
                    textAlign: 'center'
                  }}
                >
                  {resourceAllocations.totalHoursPerWeek} <span>hrs</span>
                </Typography>
              </Box>
              <Box
                sx={{
                  height: '3px',
                  width: '52px',
                  borderRadius: '10px',
                  backgroundColor:
                    resourceAllocations.totalHoursPerWeek <= 24
                      ? '#FFD747'
                      : resourceAllocations.totalHoursPerWeek <= 32
                      ? '#32B046'
                      : resourceAllocations.totalHoursPerWeek <= 40
                      ? '#FFAB47'
                      : '#FF5D47'
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '10px',
                  lineHeight: '16px',
                  letterSpacing: '0.5px',
                  fontWeight: 400,
                  color: '#94979F'
                }}
              >
                Current Projects Engaged
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: '6px'
              }}
            >
              {filteredResourceAllocationsProjects.length !== 0 ? (
                filteredResourceAllocationsProjects.map((project) => (
                  <MouseOverPopover
                    key={project.project.id}
                    colorCode={project.project.colorCode}
                    projectName={project.project.projectName}
                    projectStatus={project.project.projectStatus}
                    projectHours={project.hoursPerWeek}
                    memberStatus={project.memberStatus}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '10px',
                        padding: '5px',
                        borderRadius: '12px',
                        backgroundColor: '#295DAB0D',
                        width: 'fit-content',
                        opacity: project.memberStatus === 'APPROVED' ? 1 : 0.3
                      }}
                    >
                      <Box
                        sx={{
                          height: '22px',
                          width: '22px',
                          borderRadius: '100px',
                          backgroundColor: project.project.colorCode
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Be Vietnam Pro',
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.5px',
                          fontWeight: 400,
                          color: '#1A1B1F'
                        }}
                      >
                        {project.project.projectName}
                      </Typography>
                    </Box>
                  </MouseOverPopover>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '10px',
                    padding: '5px',
                    borderRadius: '12px',
                    backgroundColor: '#295DAB0D',
                    width: 'fit-content'
                  }}
                >
                  <Typography>No Projects</Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '10px',
                  lineHeight: '16px',
                  letterSpacing: '0.5px',
                  fontWeight: 400,
                  color: '#94979F'
                }}
              >
                Tools/Technology Competency
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: '11px'
              }}
            >
              {resourceAllocations.skills.length !== 0 ? (
                resourceAllocations.skills.slice(0, 6).map((skills) => (
                  <Box
                    key={skills.skill.id}
                    sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
                  >
                    <Box>
                      {skills.proficiencyLevel === 'EXPERT' && (
                        <ToolsIcon1
                          style={{
                            opacity: !memberSkillsIdPendingApprovals.includes(
                              skills.id ?? ''
                            )
                              ? 0.3
                              : 1
                          }}
                        />
                      )}
                      {skills.proficiencyLevel === 'INTERMEDIATE' && (
                        <ToolsIcon2
                          style={{
                            opacity: !memberSkillsIdPendingApprovals.includes(
                              skills.id ?? ''
                            )
                              ? 0.3
                              : 1
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: 'Be Vietnam Pro',
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.5px',
                          fontWeight: 400,
                          color: '#676769',
                          opacity: !memberSkillsIdPendingApprovals.includes(
                            skills.id ?? ''
                          )
                            ? 0.3
                            : 1
                        }}
                      >
                        {skills.skill.name}
                      </Typography>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '10px',
                    padding: '5px',
                    borderRadius: '12px',
                    backgroundColor: '#295DAB0D',
                    width: 'fit-content'
                  }}
                >
                  <Typography>No Tools and Technologies</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Box>
  );
};
export default ResourceAllocationCard;
